/**
 * commentLikes — the heart on a comment likes it for real.
 *
 * WHAT WAS THERE. Four hearts, none talking to the server. The detail page's
 * take heart ([data-takelike], app.js) flipped a class, a session Set keyed
 * "listingId:index" and `c.likes ± 1` on a real seller's LISTINGS row — a like
 * that vanished on reload and never reached the commenter. The comments modal's
 * heart had no handler at all, and the card preview's count was a plain span.
 * Blake 2026-09-10: it should be tappable, and fill red when liked.
 *
 * THE ROUTE IS A REAL TOGGLE. POST /api/comments/:commentId/vote with
 * { vote: "up" } inserts the viewer's vote row or deletes it in one atomic CTE
 * and answers { ok: true, liked, upvotes } (comments.ts; the count since PR
 * #874). 401 for a guest,
 * 404 for a missing comment, 409 "Voting is closed on this listing" when the
 * parent listing left an engageable status, 429 from writeLimiter. GET
 * /api/comments/:listingId already sends each comment's id and likedByMe, and
 * mapCommentRow now keeps both, so a reload paints the viewer's own state.
 *
 * 🔑 KEYED BY COMMENT ID, NOT BY INDEX. app.js's [data-takelike]="listingId:i"
 * goes stale the moment a loader re-reads the thread (the server orders by
 * upvotes, so a like can reorder it). Every heart that carries a real comment
 * now also carries data-commentlike="<id>", and that is what this module acts
 * on. [data-takelike] stays on the markup for the standalone demo, which has no
 * server, and is in writeGuard's GUARDED_WRITES here so the local fake cannot
 * run beside the real vote and count the tap twice.
 *
 * 🪤 WHEN THE ANSWER CARRIES NO `upvotes`, THE COUNT IS DERIVED FROM THE STATE
 * BEFORE THE TAP: the old one plus the change the server reports: +1 when it now says liked and we did not have it, -1 the other way,
 * nothing when both agree. A heart that was stale (liked in another tab) moves
 * by what the server did, never by an optimistic guess.
 *
 * 🔴 PAINTED FROM THE TAP, SETTLED BY THE ANSWER (card 1219177901097382,
 * 2026-10-20; this used to say NOT OPTIMISTIC). Every heart for the comment
 * and its count move the moment they are tapped; the answer settles them (its
 * `liked` and `upvotes` win, as before); a refusal puts them back and is said
 * in the server's own words. A request that never answers within
 * instantTap.ts's budget is put back too, and the thread is re-read by the
 * listing id the bridge carries rather than the vote resent (a resent toggle
 * would undo one that landed). A second tap while one is out is dropped.
 *
 * Stops propagation, like listingLikes: the heart sits inside a card whose own
 * click handler opens the listing.
 */

import { signedInOrWall } from "./guestWall";
import { awaitTap, createClock, isNoAnswer } from "./instantTap";
import type { ApiCommentRow } from "./floorTransport";

export const COMMENT_LIKE_SELECTOR = "[data-commentlike]";

/** Only for a failure the server gave no sentence for. */
export const GENERIC_FAILURE_MESSAGE = "Couldn't update that like — please try again";
export const SIGNIN_MESSAGE = "Sign in to like a comment";
/** After the budget ran out and the thread read could not say either way. */
export const UNCONFIRMED_MESSAGE = "Couldn't confirm that like. Check the heart before tapping again.";

interface CommentLikeState {
  likes: number;
  liked: boolean;
  /** The listing whose thread holds the comment: what a re-read after a timeout asks for. */
  listingId?: string;
}

interface CommentLikeWindow extends Window {
  toast?: (message: string) => void;
  __gbzCommentLikeState?: (commentId: string) => CommentLikeState | null;
  __gbzSetCommentLike?: (commentId: string, likes: number, liked: boolean) => number;
}

export function installCommentLikes(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  {
    notify = (message: string) => {
      if (!win.document.getElementById("toast")) return;
      (win as CommentLikeWindow).toast?.(message);
    },
  }: { notify?: (m: string) => void } = {},
): () => void {
  const w = win as CommentLikeWindow;
  /* In flight, per comment: with a real await, a double tap is a like racing its
     own unlike, and the second would be computed from a baseline the first moved. */
  const busy = new Set<string>();

  /** Every heart for this comment — card preview, detail row, modal — at once. */
  function paint(commentId: string, likes: number, liked: boolean): void {
    for (const el of Array.from(win.document.querySelectorAll<HTMLElement>(COMMENT_LIKE_SELECTOR))) {
      if (el.dataset.commentlike !== commentId) continue;
      el.classList.toggle("on", liked);
      el.setAttribute("aria-pressed", String(liked));
      const count = el.querySelector("i");
      if (count) count.textContent = String(likes);
    }
    /* The comments modal shows its count as text beside the heart, not on it:
       patched in place, never a repaint, because a repaint takes the half-typed
       draft in #cm-in with it (adversarial review 2026-09-11). */
    for (const line of Array.from(win.document.querySelectorAll<HTMLElement>("[data-cmcomment]"))) {
      if (line.dataset.cmcomment === commentId) line.textContent = `${likes} ${likes === 1 ? "like" : "likes"}`; // app.js likesLabel's rule
    }
  }

  /** The latest press per comment. A late answer or a re-read for an older press changes nothing. */
  const presses = new Map<string, number>();
  /* ── THE ORDER OF EVENTS (instantTap.ts header): one clock for this module. No seed here; the
     thread loaders write the rows, and `before` reads them at the tap. ── */
  const tick = createClock();
  /** The last settled state per comment (an answer or a thread read), and when. */
  const settled = new Map<string, { likes: number; liked: boolean; at: number }>();

  /** Every copy of the comment in LISTINGS, and every heart on screen, from one state. */
  function show(commentId: string, likes: number, liked: boolean): void {
    w.__gbzSetCommentLike?.(commentId, likes, liked);
    paint(commentId, likes, liked);
  }

  /** The press, settled: the state is the server's. */
  function settle(commentId: string, likes: number, liked: boolean): void {
    settled.set(commentId, { likes, liked, at: tick() });
    show(commentId, likes, liked);
  }

  /** The count after `liked` changes: the old one plus the change, never below zero, unchanged when nothing moved. */
  function derive(before: CommentLikeState, liked: boolean): number {
    if (liked === before.liked) return before.likes;
    return Math.max(0, before.likes + (liked ? 1 : -1));
  }

  /**
   * The server's answer, applied. Returns "unknown" when the toggle MAY have been
   * processed and only the thread can say (a 502/504, an unreadable 2xx).
   */
  async function apply(commentId: string, want: boolean, back: () => CommentLikeState, res: Response, still: () => boolean = () => true): Promise<"settled" | "refused" | "unknown"> {
    if (res.status === 401) {
      const b = back();
      show(commentId, b.likes, b.liked);
      notify(SIGNIN_MESSAGE);
      return "refused";
    }
    if (isNoAnswer(res.status)) {
      const b = back();
      show(commentId, b.likes, b.liked);
      return "unknown";
    }
    if (!res.ok) {
      /* Put back, then the server's words (409 "Voting is closed on this
         listing"; 429, the limiter's sentence, is one more refusal). */
      const b = back();
      show(commentId, b.likes, b.liked);
      const err = (await res.json().catch(() => ({}))) as { error?: string };
      if (!still()) return "refused";
      /* No words when the way back already shows what was asked (pass 4, finding 2). */
      if (b.liked === want) return "refused";
      notify(err.error ?? GENERIC_FAILURE_MESSAGE);
      return "refused";
    }
    /* The vote route answers the comment's own count as `upvotes` (comments.ts,
       PR #874). It wins, because a count derived from this tab's cached snapshot
       goes wrong the moment another tab or another user moved it. The field is
       `upvotes`, not `likes`: listingLikes.ts reads `likes` from a different
       route. With no count in the answer, the derived count stands, derived from
       the state BEFORE the tap, never from the guess on screen. A 200 saying the
       opposite of the tap is the toggle meeting a like made elsewhere: painted
       as the server says, and not a refusal, so nothing is said. */
    const body = (await res.json().catch(() => null)) as { liked?: boolean; upvotes?: number } | null;
    /* A LATE answer asks again after its body: a newer press can start while the body is in flight. */
    if (!still()) return "settled";
    if (!body || typeof body.liked !== "boolean") {
      /* A 2xx with an unreadable receipt: the vote landed one way or the other; read. */
      const b = back();
      show(commentId, b.likes, b.liked);
      return "unknown";
    }
    const liked = body.liked;
    const likes = typeof body.upvotes === "number" ? body.upvotes : derive(back(), liked);
    settle(commentId, likes, liked);
    return "settled";
  }

  /**
   * After the budget ran out: the comment's own thread, re-read by the listing
   * id the bridge carries, says whether the vote landed. Never the vote again
   * (decision 1): a resent toggle would undo the first one if it did land.
   */
  async function reread(commentId: string, before: CommentLikeState, want: boolean, stale: () => boolean): Promise<void> {
    const listingId = before.listingId;
    const outcome = listingId
      ? await awaitTap(authFetch(`/api/comments/${encodeURIComponent(listingId)}`))
      : null;
    const rows = outcome && outcome.kind === "answer" && outcome.res.ok
      ? ((await outcome.res.json().catch(() => null)) as ApiCommentRow[] | null)
      : null;
    /* Checked AFTER the body is read: a late answer can land between this read's
       headers and its body, and it is the truth about its own write. */
    if (stale()) return;
    const row = Array.isArray(rows) ? rows.find((r) => r?.comment?.id === commentId) : undefined;
    if (!row) {
      notify(UNCONFIRMED_MESSAGE);
      return;
    }
    const liked = row.likedByMe === true;
    settle(commentId, row.comment.upvotes ?? 0, liked);
    if (liked !== want) notify(GENERIC_FAILURE_MESSAGE);
  }

  async function toggle(commentId: string): Promise<void> {
    const before = w.__gbzCommentLikeState?.(commentId) ?? null;
    // Not a comment on the floor this page knows: nothing to count from, nothing to paint.
    if (!before) return;
    const want = !before.liked;
    /* 🔴 PAINTED FROM THE TAP (card 1219177901097382): every heart for the
       comment and its count move now; the answer settles them; a refusal, a
       dropped request or a silent one puts them back. */
    show(commentId, derive(before, want), want);
    const paintedAt = tick();
    const press = (presses.get(commentId) ?? 0) + 1;
    presses.set(commentId, press);
    /* Busy holds the control for the whole press, so only a LATE answer (after the
       press is over) has to ask whether a newer press has taken the control. */
    const current = (): boolean => presses.get(commentId) === press;
    /* The way back: this press's own thread read when it settled while a late answer
       was still out (pass 3, finding 2), else the state at the tap. */
    const back = (): CommentLikeState => {
      const s = settled.get(commentId);
      return s && s.at > paintedAt ? { likes: s.likes, liked: s.liked, listingId: before.listingId } : before;
    };

    const request = authFetch(`/api/comments/${encodeURIComponent(commentId)}/vote`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ vote: "up" }),
    });
    const outcome = await awaitTap(request);
    if (outcome.kind === "answer") {
      const verdict = await apply(commentId, want, back, outcome.res);
      /* A toggle whose answer was lost (502/504, an unreadable 2xx) may have landed: read. */
      if (verdict === "unknown") await reread(commentId, before, want, () => false);
      return;
    }
    { const b = back(); show(commentId, b.likes, b.liked); }
    if (outcome.kind === "thrown") {
      /* A dropped request can be a connection reset AFTER the vote: read rather than guess. */
      await reread(commentId, before, want, () => false);
      return;
    }
    let answered = false;
    let readDone = false;
    /* A read after the press is over: it holds the heart busy like the first one (decision 3), so
       no newer press can start while it is out and nothing can make it stale. */
    const readAgain = async (): Promise<void> => {
      if (!current() || busy.has(commentId)) return;
      busy.add(commentId);
      try { await reread(commentId, before, want, () => false); } finally { busy.delete(commentId); }
    };
    void request.then(
      async (res) => {
        /* A late 502/504 says nothing about the vote: the thread read already out decides; if it
           has already finished, read once more (the vote may have landed after it). */
        if (isNoAnswer(res.status)) { if (readDone) await readAgain(); return; }
        answered = true;
        if (!current()) return;
        const verdict = await apply(commentId, want, back, res, current);
        /* A late 2xx with an unreadable receipt: the vote landed one way or the other, and the
           read that was out has stood down (`answered`): read again (pass 4, finding 1). */
        if (verdict === "unknown") await readAgain();
      },
      () => { /* failed after the budget: the thread read is the truth */ },
    );
    await reread(commentId, before, want, () => answered);
    readDone = true;
  }

  const onClick = (event: Event): void => {
    const target = event.target as Element | null;
    if (!target?.closest) return;
    const el = target.closest(COMMENT_LIKE_SELECTOR) as HTMLElement | null;
    if (!el) return;
    const commentId = el.dataset.commentlike;
    if (!commentId) return;
    // stopPropagation, never stopImmediatePropagation: the other capture interceptors must still run.
    event.stopPropagation();
    event.preventDefault();
    // A guest gets the sign-in wall and no request — see guestWall.ts.
    if (!signedInOrWall(win)) return;
    if (busy.has(commentId)) return;
    busy.add(commentId);
    void toggle(commentId)
      .catch(() => notify(GENERIC_FAILURE_MESSAGE))
      .finally(() => busy.delete(commentId));
  };

  win.document.addEventListener("click", onClick, true);
  return () => win.document.removeEventListener("click", onClick, true);
}
